/**
 * Spaces API - Per-viewer space preferences (request / response)
 *
 * PUT /v1/spaces/:spaceId/preferences
 *
 * The unified per-viewer notify/mute write surface for ALL space kinds (section
 * + community). It is the SELF-authorized sibling of the leader-config
 * `PATCH /v1/spaces/:spaceId/settings` route (plan KTD1): this body carries only
 * the viewer's OWN notify level + mute, and the server always targets the
 * session viewer (`ctx.schoolUserId`).
 *
 * IDOR-safe by construction: there is NO `user_id` (or any other identity) field
 * on this request. The legacy `POST /v1/groups/:id/preferences` accepts an
 * unguarded `user_id` and writes whatever identity it is given — this contract
 * deliberately omits it so the unified route can never forward a caller-supplied
 * identity. The body is `.strict()` so a stray `user_id` (or any unknown key) is
 * rejected with a 400 at the contract boundary, not silently dropped.
 *
 * `muted` is a single boolean (the server maps it to follow/unfollow + the
 * canonical `UserFeedMute` store internally for community; for sections it
 * upserts/deletes `UserFeedMute` directly — KTD5). `notifyLevel` accepts the
 * legacy `'MUTED'` for one mobile release cycle (coerced to `'NONE'`
 * server-side), matching the legacy group preferences contract.
 */

import { z } from 'zod'

// ============================================================================
// PUT /v1/spaces/:spaceId/preferences
// ============================================================================

/**
 * Inbound notify level accepts the legacy `'MUTED'` for one mobile release cycle
 * (pre-migration mobile clients still submit it); the use-case coerces it to
 * `'NONE'` before persisting. Drop `'MUTED'` once mobile adoption is wide.
 */
export const SpacePreferencesNotifyLevelInputSchema = z.enum(['ALL', 'IMPORTANT', 'MENTIONS', 'NONE', 'MUTED'])

/**
 * Request body for the per-viewer preferences write. Both fields are OPTIONAL —
 * a request carries only what the viewer is changing — but at least one must be
 * present (an empty body has nothing to update). `.strict()` rejects any unknown
 * key (notably `user_id`) with a 400, so a cross-user write attempt cannot even
 * reach the use-case.
 */
export const UpdateSpacePreferencesRequestSchema = z
  .object({
    notifyLevel: SpacePreferencesNotifyLevelInputSchema.optional().describe(
      "The session viewer's own notification level for this space."
    ),
    muted: z.boolean().optional().describe("Whether the session viewer has muted this space's feed.")
  })
  .strict()
  .refine(body => body.notifyLevel !== undefined || body.muted !== undefined, {
    message: 'At least one of notifyLevel or muted must be provided.'
  })

export type UpdateSpacePreferencesRequest = z.infer<typeof UpdateSpacePreferencesRequestSchema>

/**
 * The updated per-viewer view echoed back after the write. Mirrors the viewer-
 * resolved fields on `SpaceDetailSchema.settings` (`notifyLevel` + `muted`) so a
 * client can refresh its row from the response without a re-read. `notifyLevel`
 * is nullable because a viewer may have no preference row (e.g. an admin who is
 * not a member never created one). Typed to the canonical NotificationLevel enum
 * (the four real levels) rather than any string; `.catch(null)` keeps it forward/
 * skew-safe — an unknown future level coerces to null instead of failing the parse.
 * The server coerces legacy `'MUTED'` to `'NONE'` before persisting, so the wire
 * value is always one of {NONE,MENTIONS,IMPORTANT,ALL} or null.
 */
export const SpacePreferencesSchema = z.object({
  spaceId: z.string(),
  kind: z.enum(['section', 'community']),
  notifyLevel: z.string().nullable(),
  muted: z.boolean()
})

export type SpacePreferences = z.infer<typeof SpacePreferencesSchema>

export const UpdateSpacePreferencesResponseSchema = z.object({
  data: SpacePreferencesSchema,
  message: z.string()
})

export type UpdateSpacePreferencesResponse = z.infer<typeof UpdateSpacePreferencesResponseSchema>
